import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  Inbox,
  Calendar as CalendarIconLucide,
  History,
  Utensils,
  Wallet,
  ChevronRight,
} from "lucide-react";
import { motion } from "motion/react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { DemandesPanel } from "./-components/DemandesPanel";
import { HistoryList } from "./-components/HistoryList";
import { chefQuoteRequestQueries } from "@/domain/chef-quote-request/chef-quote-request.query";
import { Pagination } from "@/components/ui/pagination";
import type { Booking, BookingFilters } from "@/domain/booking/booking.type";
import { bookingQueries } from "@/domain/booking/booking.query";
import { BookingDetailSheet } from "./-components/layout/BookingDetailSheet";
import { fr } from "date-fns/locale";
import { BookingStatuses } from "@hometoq/backend/enums";

type TabValue = "a-traiter" | "a-venir" | "historique";

const TAB_VALUES: TabValue[] = ["a-traiter", "a-venir", "historique"];

export const Route = createFileRoute("/dashboard/chef/reservations")({
  validateSearch: (search: Record<string, unknown>): { tab?: TabValue } => {
    const tab = search.tab;
    if (typeof tab === "string" && TAB_VALUES.includes(tab as TabValue)) {
      return { tab: tab as TabValue };
    }
    return {};
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { tab } = Route.useSearch();
  const activeTab: TabValue = tab ?? "a-traiter";

  const { data: quoteRequests } = useQuery(chefQuoteRequestQueries.myList());
  const pendingDemandesCount =
    quoteRequests?.filter((r) => r.status === "pending").length ?? 0;
  const validatedCount =
    quoteRequests?.filter((r) => r.status === "validated").length ?? 0;
  const proposalSentCount =
    quoteRequests?.filter((r) => r.status === "proposal_sent").length ?? 0;
  const inFlightCount = validatedCount + proposalSentCount;

  const handleTabChange = (value: string) => {
    navigate({
      search: { tab: value === "a-traiter" ? undefined : (value as TabValue) },
      replace: true,
    });
  };

  return (
    <section className="p-4">
      <h1 className="font-h1 text-center text-dark-blue">Réservations</h1>
      <h4 className="font-h4 text-center text-muted-foreground">
        Demandes, prestations à venir et historique
      </h4>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="mt-6 max-w-5xl mx-auto"
      >
        <TabsList className="mx-auto mb-5">
          <TabsTrigger value="a-traiter" className="gap-1.5">
            <Inbox size={14} />
            À traiter
            {pendingDemandesCount > 0 && (
              <span className="ml-1 px-1.5 rounded-full bg-primary text-white text-[10px] font-bold leading-none py-0.5">
                {pendingDemandesCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="a-venir" className="gap-1.5">
            <CalendarIconLucide size={14} />À venir
            {inFlightCount > 0 && (
              <span className="ml-1 px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-bold leading-none py-0.5">
                {inFlightCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="historique" className="gap-1.5">
            <History size={14} />
            Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="a-traiter" className="mt-0">
          <DemandesPanel
            showHeader={false}
            statusFilter={["pending"]}
            emptyTitle="Rien à traiter pour l'instant"
            emptyDescription="Quand un client vous enverra une demande de devis, elle apparaîtra ici. Vous serez aussi prévenu·e par email."
          />
        </TabsContent>

        <TabsContent value="a-venir" className="mt-0">
          <UpcomingTab
            hasValidatedAwaitingClient={validatedCount > 0}
            hasProposalsInWaiting={proposalSentCount > 0}
          />
        </TabsContent>

        <TabsContent value="historique" className="mt-0">
          <HistoryList />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function UpcomingTab({
  hasValidatedAwaitingClient,
  hasProposalsInWaiting,
}: {
  hasValidatedAwaitingClient: boolean;
  hasProposalsInWaiting: boolean;
}) {
  return (
    <div className="flex flex-col gap-8">
      {hasValidatedAwaitingClient && (
        <section>
          <header className="mb-3">
            <h2 className="font-h4 text-dark-blue">
              Demandes validées · en attente du client
            </h2>
            <p className="text-xs text-muted-foreground">
              Vous avez accepté ces demandes. Le client doit confirmer et
              payer pour que la prestation devienne une réservation ferme.
            </p>
          </header>
          <DemandesPanel showHeader={false} statusFilter={["validated"]} />
        </section>
      )}

      {hasProposalsInWaiting && (
        <section>
          <header className="mb-3">
            <h2 className="font-h4 text-dark-blue">
              Propositions sur-mesure en attente
            </h2>
            <p className="text-xs text-muted-foreground">
              Vous avez envoyé un devis. Le client doit confirmer pour que la
              prestation devienne une réservation payée.
            </p>
          </header>
          <DemandesPanel
            showHeader={false}
            statusFilter={["proposal_sent"]}
          />
        </section>
      )}

      <section>
        <header className="mb-3">
          <h2 className="font-h4 text-dark-blue">Réservations confirmées</h2>
          <p className="text-xs text-muted-foreground">
            Prestations à honorer, payées par les clients.
          </p>
        </header>
        <UpcomingBookingsList />
      </section>
    </div>
  );
}

function UpcomingBookingsList() {
  const [filters, setFilters] = useState<BookingFilters>({
    name: "",
    priceSort: null,
    dateSort: null,
    date: null,
    statusSort: null,
    status: BookingStatuses.ACCEPTED,
    page: 1,
  });
  const { data, isLoading } = useQuery(bookingQueries.list(filters));
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null
  );

  const setPage = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const bookings = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 rounded-2xl bg-white border border-border animate-pulse"
            />
          ))}
        </div>
      )}

      {!isLoading && bookings.length === 0 && (
        <div className="rounded-3xl border-2 border-dashed border-border bg-white p-10 text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-3">
            <CalendarIconLucide size={20} className="text-primary/60" />
          </div>
          <h3 className="font-h4 text-dark-blue mb-1">
            Aucune prestation à venir
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Vos prestations confirmées et payées apparaîtront ici.
          </p>
        </div>
      )}

      {!isLoading && bookings.length > 0 && (
        <div className="flex flex-col gap-3">
          {bookings.map((booking, i) => (
            <motion.div
              key={booking.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.03 }}
            >
              <BookingCard
                booking={booking}
                onClick={() => setSelectedBookingId(booking.id)}
              />
            </motion.div>
          ))}
        </div>
      )}

      {data?.meta && data.meta.lastPage > 1 && (
        <Pagination
          currentPage={data.meta.currentPage}
          totalPages={data.meta.lastPage}
          onPageChange={setPage}
        />
      )}

      <BookingDetailSheet
        bookingId={selectedBookingId}
        isOpen={selectedBookingId !== null}
        onClose={() => setSelectedBookingId(null)}
      />
    </div>
  );
}

function BookingCard({
  booking,
  onClick,
}: {
  booking: Booking;
  onClick: () => void;
}) {
  const eventDate = new Date(booking.date);
  const contact = booking.bookingContact;
  const formattedDate = format(eventDate, "EEEE d MMMM yyyy", { locale: fr });
  const price = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(booking.totalPrice / 100);
  const distance = formatDistanceToNow(eventDate, {
    locale: fr,
    addSuffix: true,
  });

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full bg-white rounded-2xl border border-border overflow-hidden hover:border-primary/40 hover:shadow-sm transition-all text-left"
    >
      <div className="px-4 py-3.5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-100 text-emerald-700">
          <Utensils size={18} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              Réservation confirmée
            </span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-200">
              Payée
            </span>
          </div>
          <p className="text-sm font-bold text-dark-blue truncate">
            {contact.firstName} {contact.lastName}
            {booking.menu?.name ? ` · ${booking.menu.name}` : ""}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 capitalize flex items-center gap-x-2 flex-wrap">
            <span>{formattedDate}</span>
            <span className="text-border">•</span>
            <span className="inline-flex items-center gap-1 text-dark-blue/70 font-medium normal-case">
              <Wallet size={11} /> {price}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:block text-[11px] text-muted-foreground">
            {distance}
          </span>
          <ChevronRight size={16} className="text-muted-foreground" />
        </div>
      </div>
    </button>
  );
}
